import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Col,
  Heading,
  Row,
  Table,
  TableColumnInstance,
  TipBox,
} from '@orobix/orobix-fe-components'
import { includes } from 'ramda'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import CounterName from './CounterName'
import useStyles from './CountersList.styles'
import { Counter } from './types'
import { useCountersData } from './useCountersData'
import { getCounterStatus } from './utils'

const limitReachedI18nKeys = {
  MAX_DOWNLOADS: 'Downloads are now disabled.' as const,
  MAX_TRAININGS: 'Trainings are now disabled.' as const,
  MAX_TRAINING_TESTS: 'Tests are now disabled.' as const,
}

const CounterList = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { counters, isFetching } = useCountersData()

  const columns: TableColumnInstance<Counter, keyof Counter>[] = [
    {
      id: 'code',
      header: t('Counter'),
      template: useCallback(
        (counter: Counter) => (
          <>
            <CounterName counter={counter} />
            {includes(getCounterStatus(counter), [
              'limit_reached',
              'near_limit',
            ]) && <br />}
            {
              {
                ok: '',
                unlimited: '',
                near_limit: (
                  <span className={classes.alertMessage}>
                    {t('Warning, you are reaching the limit.')}
                  </span>
                ),
                limit_reached: (
                  <span className={classes.alertMessage}>
                    {`${t('Warning, you reached the limit.')} ${t(
                      limitReachedI18nKeys[counter.code]
                    )}`}
                  </span>
                ),
              }[getCounterStatus(counter)]
            }
          </>
        ),
        [classes.alertMessage, t]
      ),
    },
    {
      id: 'currentValue',
      header: t('Count'),
      template: useCallback(
        (row: Counter) =>
          ({
            ok: row.currentValue,
            near_limit: (
              <span className={classes.alert}>{row.currentValue}</span>
            ),
            limit_reached: (
              <span className={classes.alert}>
                {row.currentValue}{' '}
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  title={`${t('Warning, you reached the limit.')} ${
                    limitReachedI18nKeys[row.code]
                  }`}
                />
              </span>
            ),
            unlimited: '-',
          }[getCounterStatus(row)]),
        [classes.alert, t]
      ),
    },
    {
      id: 'threshold',
      header: t('Limit'),
      template: useCallback(
        (row: Counter) => (row.isEnabled ? row.threshold : t('Unlimited')),
        [t]
      ),
    },
  ]
  return (
    <>
      <Heading headingLevel="1" headingLevelStyle="3">
        {t('Counters')}
      </Heading>

      <Row>
        <Col xl={6}>
          <Table
            columns={columns}
            data={counters}
            alternateRows
            isEmpty={!isFetching && counters.length === 0}
            emptyMessage={t('No counters found.')}
            isLoading={isFetching}
            loadingMessage={t('Loading')}
          />
        </Col>
      </Row>
      <Row>
        <Col xl={6}>
          <TipBox>
            {t(
              'These limits are in place to prevent excessive usage of the platform and are not intended to be restrictive, but rather to prevent any misuse of the system. If your license grants you unlimited access to the platform, these limits can be reset anytime by Oròbix upon a user request.'
            )}
          </TipBox>
        </Col>
      </Row>
    </>
  )
}

export default CounterList
